import { useEffect, useRef, useState } from 'react';
import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import api from '@/shared/api/client';

/**
 * Le champ qui identifie une machine, et la seule preuve qu'elle existe.
 *
 * Avant le 22/08/2026, ce champ était du texte libre. Un poseur pouvait recopier une
 * étiquette de travers, faire signer le mandat et repartir : personne ne s'apercevait
 * de rien avant que la supervision ne cherche un équipement qui n'a jamais existé,
 * des semaines plus tard, chez le client. Le bouton « tester la connexion » qui
 * existait alors répondait « connecté avec succès » 85 fois sur 100 à partir d'un
 * hachage de la saisie, sans jamais parler à quoi que ce soit.
 *
 * Désormais un seul geste : le poseur tape l'identifiant, l'application interroge les
 * deux canaux réels (le cloud Tuya, le broker MQTT), et remplit elle-même ce que la
 * machine sait dire d'elle : la marque quand le broker la connaît, le modèle.
 *
 * ⚠️ CE QUE LE POSEUR NE LIT PLUS, DEPUIS LE 27/10 (lot L5, Loom de David du 13/10).
 *
 * Ni le canal (« Cloud Tuya », « Broker MQTT ») ni la marque « Tuya » : le poseur ne
 * connaît pas Tuya, le mot l'égare, et le canal est une donnée d'exploitant dont il ne
 * fait rien. Le serveur renvoie désormais une marque VIDE pour un appareil Tuya (Tuya
 * n'est pas un fabricant) ; la ligne Marque ne s'affiche donc que si elle dit quelque
 * chose, et le poseur tape la marque qu'il lit sur l'appareil.
 *
 * Et l'étiquette n'est citée que là où elle sert. L'ID d'un thermostat, d'un ballon ou
 * d'un radiateur est associé au compte du client dans son application : il ne se lit
 * sur aucune étiquette, et « comparez-le à l'étiquette » envoyait chercher au mauvais
 * endroit. Le numéro d'une batterie ou l'ID central d'un PV, lui, se recopie bien d'une
 * étiquette : le rappel le dit, avec le geste « caractère par caractère ».
 *
 * ⚠️ UNE RÉPONSE N'EST PLUS EXIGÉE POUR AVANCER, DEPUIS LE 30/08.
 *
 * Jusque-là, l'étape ne se validait pas tant qu'aucun appareil n'avait répondu. Un
 * poseur dont la machine était muette ne dépassait pas la deuxième étape : il ne voyait
 * ni l'offre, ni le mandat, ni la fin de chantier, et son dossier n'existait nulle part.
 * David a tranché l'inverse — le poseur saisit l'identifiant qu'il a relevé, le produit
 * lui DIT ce qu'il en pense, et le laisse continuer dans tous les cas.
 *
 * CE QUI DISPARAÎT EST LA PORTE, PAS LA MESURE. Ce champ interroge toujours les deux
 * canaux, affiche toujours son verdict, et rappelle toujours ce qui a été tapé avec son
 * nombre de caractères. Il dit en plus ce que le silence entraîne : le chantier partira
 * « en attente de matériel », et il faudra rattacher l'appareil depuis le bureau.
 *
 * POURQUOI IL N'Y A PLUS DE BOUTON « VÉRIFIER »
 *
 * Tranché à l'appel du 27/08/2026. Un bouton fait de la vérification un geste FACULTATIF
 * qu'on peut oublier, et sa présence laissait croire que ne pas le presser était une
 * option. La vérification n'a pas disparu, elle a cessé d'être un choix : elle part
 * toute seule dès que la saisie se stabilise, et le serveur la refait de toute façon au
 * moment de signer (`services/cloture.controler_materiel`).
 *
 * Le délai de stabilisation existe pour une raison précise : sans lui, chaque frappe
 * partirait interroger le cloud Tuya, soit une douzaine d'allers-retours pour un seul
 * numéro de série.
 *
 * POURQUOI LA VÉRIFICATION SE PÉRIME
 *
 * Le verdict est rangé avec l'identifiant sur lequel il porte. Si le poseur corrige un
 * chiffre après coup, `aJour` redevient faux et le badge disparaît. Sans ça, il
 * suffirait de vérifier un identifiant valide puis d'en saisir un autre pour passer :
 * l'écran afficherait toujours sa coche verte.
 *
 * CE QUE LA COULEUR DOIT DIRE, DEPUIS QUE LA PORTE EST TOMBÉE
 *
 * Un appareil introuvable s'affichait en rouge, et c'était juste tant qu'il arrêtait le
 * poseur. Il ne l'arrête plus : l'écran annonçait donc une faute une ligne avant de
 * dire « vous pouvez continuer ». Il porte désormais l'ambre — la couleur que l'état
 * « en attente de matériel » a déjà sur le bandeau de fin de chantier, dans la liste
 * des chantiers en attente et dans le compte de l'accueil.
 *
 * Un seul verdict reste rouge, `deja_associe` : celui-là ne parle pas d'une machine
 * muette mais d'une machine qui répond à quelqu'un d'autre. C'est un conflit à
 * trancher, pas un rattrapage à prévoir.
 *
 * CE QUE L'ÉCRAN DOIT DIRE, ET QU'IL NE DISAIT PAS
 *
 * Une saisie qui ne correspond à aucune machine doit le dire AVEC LE NUMÉRO SAISI sous
 * les yeux. En pré-production, une batterie enrôlée sous `28045340019` (onze chiffres)
 * publiait en réalité sous `280425340019` (douze) : un « 2 » manquant, et une
 * télémétrie muette pour toujours, sans que rien à l'écran ne le laisse voir.
 *
 * POURQUOI UN `tid` SÉPARÉ DU `type`, DEPUIS LE 28/08/2026
 *
 * Un chantier porte désormais PLUSIEURS radiateurs, donc plusieurs de ces champs sur le
 * même écran. Leurs `data-testid` se seraient tous appelés `radiateur-identifiant`, et
 * une recette navigateur aurait piloté le premier en croyant piloter le troisième.
 * Le `type` ne pouvait pas servir de discriminant : il reste le mot AFFICHÉ au poseur
 * quand l'appareil s'annonce sous un autre type (« pas comme "Radiateur" »).
 */

const ETATS = {
  en_ligne: {
    Icone: CheckCircle2, couleur: 'text-emerald-600',
    cadre: 'border-emerald-200 bg-emerald-50/60',
  },
  hors_ligne: {
    Icone: AlertTriangle, couleur: 'text-amber-600',
    cadre: 'border-amber-200 bg-amber-50/60',
  },
  // ⚠️ PLUS DE ROUGE ICI, ET C'EST UNE CONSÉQUENCE DU LOT R.
  //
  // Tant que l'étape refusait d'avancer sur un appareil muet, le rouge disait vrai :
  // le poseur était arrêté. Depuis le 30/08 il ne l'est plus — il lit le verdict, il
  // continue, et le chantier part « en attente de matériel ». Garder le rouge revenait
  // à annoncer une faute juste avant de dire « vous pouvez continuer », et à faire
  // chercher un coupable là où il n'y a qu'un rattrapage à prévoir.
  //
  // L'ambre est la couleur de cet état partout ailleurs : le bandeau de la page de fin
  // de chantier, la liste « En attente de matériel », le compte de l'accueil. Un
  // équipement introuvable rejoint donc `hors_ligne`, dont il partage le sort exact —
  // rien n'a répondu, il faudra le rattacher depuis le bureau.
  introuvable: {
    Icone: AlertTriangle, couleur: 'text-amber-600',
    cadre: 'border-amber-200 bg-amber-50/60',
  },
  // ⚠️ CELUI-CI RESTE ROUGE, ET LA NUANCE EST LE SUJET. Les quatre autres verdicts
  // parlent d'une machine qui n'a pas répondu ; celui-ci parle d'une machine qui
  // répond à QUELQU'UN D'AUTRE. Ce n'est pas un rattrapage à prévoir, c'est un conflit
  // à trancher : ou le poseur s'est trompé d'identifiant, ou deux dossiers se disputent
  // le même appareil. Le peindre en ambre le rangerait parmi les incomplets, et il
  // finirait rattrapé tout seul sur le dos d'un autre client.
  deja_associe: {
    Icone: XCircle, couleur: 'text-red-600', cadre: 'border-red-200 bg-red-50/60',
  },
  // Notre lien est en cause, pas la machine : le ton doit le dire, sinon le poseur
  // démonte un équipement qui va très bien.
  indisponible: {
    Icone: AlertTriangle, couleur: 'text-neutral-600',
    cadre: 'border-neutral-200 bg-neutral-50',
  },
};

// En deçà, aucun identifiant réel : inutile d'aller déranger Tuya à chaque frappe.
const LONGUEUR_MINIMALE = 4;

// Les types dont l'identifiant est un ID d'appareil associé au compte du client (David,
// 13/10 : « ce n'est pas l'étiquette qui identifie le radiateur »), et pas un numéro lu
// sur une étiquette. Leur rappel ne parle donc pas d'étiquette. Batterie et PV n'y sont
// pas : leur numéro se recopie bien d'une étiquette.
const TYPES_PILOTABLES = ['Ballon', 'PAC', 'Radiateur'];

// Le temps qu'une saisie doit rester stable avant qu'on aille interroger le matériel.
// Assez long pour ne pas partir entre deux chiffres, assez court pour que le poseur
// n'ait pas l'impression que rien ne se passe.
const STABILISATION_MS = 700;

export default function ChampIdentifiant({
  type, v, upE, t, cle = 'identifiant_equipement',
  libelle = 'Identifiant matériel', aide, tid,
}) {
  const trad = t || ((c, secours) => secours || c);
  // Le préfixe des `data-testid`. Par défaut le type, comme depuis toujours ; explicite
  // quand plusieurs champs du même type cohabitent sur le même écran.
  const prefixe = tid || type.toLowerCase();
  const [enCours, setEnCours] = useState(false);
  // Lot L4 (relecture PR #274) : un 429 n'est pas un verdict sur l'appareil. On ne le
  // garde PAS comme verdict (il afficherait « en attente de matériel » et ne serait
  // jamais revérifié) : on dit d'attendre, et on relance seul après le délai du serveur.
  const [attente, setAttente] = useState(null);
  const [relance, setRelance] = useState(0);
  const minuteurRelance = useRef(null);
  // Un 429 peut arriver APRÈS le démontage (étape quittée) : sans ce drapeau, il armerait
  // une relance que plus personne ne nettoie.
  const monte = useRef(true);
  useEffect(() => () => { monte.current = false; clearTimeout(minuteurRelance.current); }, []);
  const identifiant = (v[cle] || '').trim();
  const verdict = v.verification;
  // Un verdict ne vaut que pour l'identifiant sur lequel il a été rendu.
  const aJour = !!verdict && verdict.identifiant === identifiant;
  const etat = aJour ? (ETATS[verdict.statut] || ETATS.indisponible) : null;
  const pilotable = TYPES_PILOTABLES.includes(type);

  // Ce que le champ contient AU MOMENT où la réponse revient. Sans cette référence, une
  // réponse lente sur un identifiant abandonné viendrait écraser le verdict d'un
  // identifiant plus récent, et l'écran validerait l'étape sur une machine que le
  // poseur ne saisit plus.
  const dernierDemande = useRef('');

  useEffect(() => {
    if (identifiant.length < LONGUEUR_MINIMALE) return undefined;
    if (aJour) return undefined;
    const minuteur = setTimeout(async () => {
      dernierDemande.current = identifiant;
      setEnCours(true);
      try {
        const { data } = await api.post('/enrolement-materiel/verifier', { identifiant });
        if (dernierDemande.current !== identifiant) return;
        upE(type, 'verification', { ...data, identifiant });
        // On ne remplit que ce que la machine a réellement dit. Écraser une saisie du
        // poseur avec une chaîne vide lui ferait perdre ce qu'il vient d'entrer.
        if (data.materiel?.marque) upE(type, 'marque', data.materiel.marque);
        if (data.materiel?.modele) upE(type, 'modele', data.materiel.modele);
      } catch (erreur) {
        if (dernierDemande.current !== identifiant) return;
        if (erreur?.response?.status === 429) {
          if (!monte.current) return;
          const delaiS = Math.max(1, parseInt(erreur.response.headers?.['retry-after'], 10) || 60);
          // Une seule consigne : la pause et sa reprise. Le « Réessayez » du serveur
          // contredirait la relance automatique.
          setAttente({ identifiant, minutes: Math.ceil(delaiS / 60) });
          clearTimeout(minuteurRelance.current);
          minuteurRelance.current = setTimeout(() => setRelance((n) => n + 1), delaiS * 1000);
          return;
        }
        upE(type, 'verification', {
          identifiant, statut: 'indisponible', vivant: false, materiel: {},
          message: 'Vérification impossible : réseau ou service indisponible.',
        });
      } finally {
        if (dernierDemande.current === identifiant) setEnCours(false);
      }
    }, STABILISATION_MS);
    return () => clearTimeout(minuteur);
    // `upE` est recréé à chaque rendu du parent : le mettre en dépendance relancerait
    // la vérification en boucle. Seul l'identifiant décide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifiant, aJour, type, cle, relance]);

  return (
    <div className="space-y-2">
      <Label className="text-[11px] text-neutral-500">
        {trad('lbl_identifiant_materiel', libelle)} *
      </Label>
      <Input
        className="h-11 text-[14px] font-mono"
        value={v[cle] || ''}
        onChange={(e) => upE(type, cle, e.target.value.trim())}
        spellCheck="false"
        autoCapitalize="none"
        autoCorrect="off"
        data-testid={`${prefixe}-identifiant`}
      />

      {aide && !aJour && !enCours && (
        <p className="text-[10.5px] text-neutral-500">{aide}</p>
      )}

      {!enCours && !aJour && attente?.identifiant === identifiant && (
        <p className="text-[11px] text-neutral-600"
           data-testid={`${prefixe}-verification-en-attente`}>
          Vérification en pause : elle reprendra d’elle-même dans {attente.minutes} min.
        </p>
      )}

      {enCours && !aJour && (
        <p className="text-[11px] text-neutral-500 flex items-center gap-1.5"
           data-testid={`${prefixe}-verification-en-cours`}>
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Recherche de l&apos;équipement…
        </p>
      )}

      {aJour && (
        <div
          className={`rounded-md border p-3 ${etat.cadre}`}
          data-testid={`${prefixe}-verdict`}
          data-statut={verdict.statut}
        >
          <p className={`text-[12.5px] font-medium flex items-start gap-1.5 ${etat.couleur}`}>
            <etat.Icone className="w-4 h-4 mt-px shrink-0" />
            <span>{verdict.message}</span>
          </p>
          {/* Ce qui a été SAISI, sous les yeux, quand rien n'a répondu. C'est le seul
              moment où le poseur peut encore le relire : une fois le mandat signé, plus
              personne ne rouvrira cet écran. L'étiquette n'est citée que pour un numéro
              qui s'y lit (batterie, PV) : voir `TYPES_PILOTABLES`. */}
          {!verdict.vivant && (
            <p className="mt-1.5 text-[11px] text-neutral-600"
               data-testid={`${prefixe}-identifiant-rappel`}>
              {pilotable ? 'ID saisi :' : 'Numéro saisi :'}{' '}
              <span className="font-mono font-medium text-neutral-900">{identifiant}</span>
              {' '}({identifiant.length} caractères).
              {!pilotable && (
                <>
                  {' '}Comparez-le au numéro de série de l&apos;étiquette,
                  {' '}caractère par caractère.
                </>
              )}
            </p>
          )}
          {/* Ce que le silence entraîne. Le poseur peut continuer — c'est la décision
              du 30/08 — mais il ne doit pas croire que son dossier est complet. */}
          {!verdict.vivant && (
            <p className="mt-1.5 text-[11px] text-neutral-600"
               data-testid={`${prefixe}-suite-si-muet`}>
              Vous pouvez continuer : le chantier partira{' '}
              <span className="font-medium">en attente de matériel</span> et
              l&apos;appareil sera rattaché depuis le bureau.
            </p>
          )}
          {verdict.vivant && (
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
              {/* Pas de « – » à la place d'une marque vide : il laisserait croire à une
                  marque inconnue, alors que c'est au poseur de la saisir (voir l'en-tête). */}
              {verdict.materiel.marque && (
                <>
                  <dt className="text-neutral-500">Marque</dt>
                  <dd className="text-neutral-800 font-medium">{verdict.materiel.marque}</dd>
                </>
              )}
              <dt className="text-neutral-500">Modèle</dt>
              <dd className="text-neutral-800 font-medium">{verdict.materiel.modele || '–'}</dd>
              {verdict.materiel.version_logicielle && (
                <>
                  <dt className="text-neutral-500">Version</dt>
                  <dd className="text-neutral-800">{verdict.materiel.version_logicielle}</dd>
                </>
              )}
            </dl>
          )}
          {/* Le type que l'appareil annonce peut contredire le bloc dans lequel le
              poseur l'a saisi. On le DIT sans rien déplacer : basculer l'équipement de
              bloc tout seul lui ferait perdre les champs déjà remplis, et le laisser
              passer en silence rangerait un ballon dans le profil d'une pompe à
              chaleur — ce qui décide de ce qu'on a le droit de piloter. */}
          {verdict.vivant && verdict.materiel.type_suggere
            && verdict.materiel.type_suggere !== type && (
            <p className="mt-2 text-[11px] text-amber-700" data-testid={`${prefixe}-type-discordant`}>
              L&apos;appareil s&apos;annonce comme « {verdict.materiel.type_suggere} », pas
              comme « {type} ». Vérifiez que vous le saisissez dans le bon bloc.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
